import {
  Form,
  Button,
  Container,
  InputProps,
  Checkbox,
  Box,
  Grid,
  SpaceBetween,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../app/hook";
import { MFA_SKIP_DAYS } from "../../configs/constants";
import {
  SUB_DOMAIN_DCM,
  SUB_DOMAIN_GENERAL,
  SUB_DOMAIN_GW,
} from "../../configs/configs";
import {
  clearAllErrTxts,
  clearFormFieldErrTxt,
  createFormErrTxt,
  createFormFieldErrTxts,
  InputNames,
} from "../error/validation-slice";
import {
  isNull,
  isPasswordPolicyValid,
  getErrorText,
} from "../../utils/inputValidation";
import { UsernameField, PasswordField, CodeField } from "./InputFields";
import PasswordPolicy from "./PasswordPolicy";
import { LABELS, INPUT_NAMES, COGNITO_ERR_MSG } from "./constants";
import { api } from "../api";
import { BASE_URL } from "../../configs/configs";
import axios from "axios";

const Screen = {
  top: 0,
  mfa: 1,
  firstLogin: 2,
  chgPassEmail: 3,
  chgPassNewPass: 4,
};

type ScreenType = (typeof Screen)[keyof typeof Screen];

interface FormAreaProps {
  description: string;
  children: JSX.Element | JSX.Element[];
}

const FormArea = ({ description, children }: FormAreaProps) => {
  const { formErrTxt } = useAppSelector((state) => state.validation);
  return (
    <Form
      header={
        <Box textAlign="center">
          <Box variant="h1">{LABELS.TITLE}</Box>
          <Box variant="p">{description}</Box>
        </Box>
      }
      errorText={formErrTxt}
    >
      {children}
    </Form>
  );
};

const SignIn = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [user, setUser] = useState<string>("");
  const [ipAddress, setIpAddress] = useState<string>("");
  const [checked, setChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [screenType, setScreenType] = useState<ScreenType>(Screen.top);

  const pathAfterLogin: string = useAppSelector((state) => state.location.path);
  const { formFieldErrTxts } = useAppSelector((state) => state.validation);
  const dispatch = useAppDispatch();

  // dish without mfa
  useEffect(() => {
    const fetchIp = async () => {
      const res = await axios.get(BASE_URL + "users/ipAddress");
      setIpAddress(res.data);
    };
    // retrieve ip
    fetchIp();

    return () => {
      dispatch(clearAllErrTxts());
    };
  }, [dispatch]);

  const userTypeLabel = (hostname: string) => {
    switch (hostname) {
      case SUB_DOMAIN_DCM:
        return "ドコモ";
      case SUB_DOMAIN_GW:
        return "GW事業者";
      case SUB_DOMAIN_GENERAL:
        return "加盟店";
      default:
        return "";
    }
  };

  /**
   * 各入力フィールドの入力チェック関数
   * @param value
   */
  const checkInputValidation = (
    value: string,
    inputName: keyof InputNames
  ): boolean => {
    let notNullMessageLabel = "";
    // メッセージラベル設定
    switch (inputName) {
      case INPUT_NAMES.EMAIL_ADDRESS:
        notNullMessageLabel = LABELS.EMAIL_ADDRESS;
        break;
      case INPUT_NAMES.PASSWORD:
        notNullMessageLabel = LABELS.PASSWORD;
        break;
      case INPUT_NAMES.NEW_PASSWORD:
        notNullMessageLabel = LABELS.NEW_PASSWORD;
        break;
      case INPUT_NAMES.NEW_PASSWORD_CONFIRMATION:
        notNullMessageLabel = LABELS.NEW_PASSWORD_CONFIRMATION;
        break;
      case INPUT_NAMES.VERIFICATION_CODE:
        notNullMessageLabel = LABELS.VERIFICATION_CODE;
        break;
      default:
        return false;
    }

    // 入力チェック
    if (isNull(value)) {
      // 必須チェック
      dispatch(
        createFormFieldErrTxts({
          name: inputName,
          msgId: "KGRERR023",
          msgArgs: [notNullMessageLabel],
        })
      );
      return false;
    } else if (
      inputName === INPUT_NAMES.NEW_PASSWORD &&
      !isPasswordPolicyValid(value)
    ) {
      // 新規パスワードのパスワード要件チェック
      dispatch(
        createFormFieldErrTxts({
          name: inputName,
          msgId: "KGRERR043",
          msgArgs: [],
        })
      );
      return false;
    } else {
      dispatch(clearFormFieldErrTxt(inputName));
      return true;
    }
  };

  /**
   * メール入力関数
   */
  const handleUsernameOnChange = ({
    detail,
  }: {
    detail: InputProps.ChangeDetail;
  }) => {
    setUsername(detail.value);
    checkInputValidation(detail.value, INPUT_NAMES.EMAIL_ADDRESS);
  };

  /**
   * パスワード入力関数
   */
  const handlePasswordOnChange = ({
    detail,
  }: {
    detail: InputProps.ChangeDetail;
  }) => {
    setPassword(detail.value);
    const inputName: keyof InputNames =
      screenType === Screen.top
        ? INPUT_NAMES.PASSWORD
        : INPUT_NAMES.NEW_PASSWORD;
    checkInputValidation(detail.value, inputName);
  };

  /**
   * パスワード確認入力関数
   */
  const handleConfirmPasswordOnChange = ({
    detail,
  }: {
    detail: InputProps.ChangeDetail;
  }) => {
    setConfirmPassword(detail.value);
    checkInputValidation(detail.value, INPUT_NAMES.NEW_PASSWORD_CONFIRMATION);
  };

  /**
   * コード入力関数
   */
  const handleCodeOnChange = ({
    detail,
  }: {
    detail: InputProps.ChangeDetail;
  }) => {
    setCode(detail.value);
    checkInputValidation(detail.value, INPUT_NAMES.VERIFICATION_CODE);
  };

  /**
   * 画面ごとの入力チェック
   * @param screenType
   * @returns
   */
  const validationCheck = (screenType: ScreenType): boolean => {
    switch (screenType) {
      case Screen.top:
        const usernameCheckResult = checkInputValidation(
          username,
          INPUT_NAMES.EMAIL_ADDRESS
        );
        const passwordCheckResult = checkInputValidation(
          password,
          INPUT_NAMES.PASSWORD
        );
        return usernameCheckResult && passwordCheckResult;
      case Screen.mfa:
        return checkInputValidation(code, INPUT_NAMES.VERIFICATION_CODE);
      case Screen.firstLogin:
        const changedPasswordCheckResult = checkInputValidation(
          password,
          INPUT_NAMES.NEW_PASSWORD
        );
        const changedPasswordConfCheckResult = checkInputValidation(
          confirmPassword,
          INPUT_NAMES.NEW_PASSWORD_CONFIRMATION
        );
        return changedPasswordCheckResult && changedPasswordConfCheckResult;
      case Screen.chgPassEmail:
        return checkInputValidation(username, INPUT_NAMES.EMAIL_ADDRESS);
      case Screen.chgPassNewPass:
        const codeCheckResult = checkInputValidation(
          code,
          INPUT_NAMES.VERIFICATION_CODE
        );
        const newPasswordCheckResult = checkInputValidation(
          password,
          INPUT_NAMES.NEW_PASSWORD
        );
        const newPasswordConfCheckResult = checkInputValidation(
          confirmPassword,
          INPUT_NAMES.NEW_PASSWORD_CONFIRMATION
        );
        return (
          codeCheckResult &&
          newPasswordCheckResult &&
          newPasswordConfCheckResult
        );
      default:
        return false;
    }
  };

  /**
   * パスワードリセットボタンクリック
   * @param event
   */
  const handleForgotPasswordBtnOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    setScreenType(Screen.chgPassEmail);
    setUsername("");
    setPassword("");
    setCode("");
    dispatch(clearAllErrTxts());
  };

  /**
   * エラー処理
   * @param err
   */
  const handleErr = (err: Error) => {
    switch (err.message) {
      case COGNITO_ERR_MSG.wrongPassword:
      case COGNITO_ERR_MSG.userNotFound:
        if (screenType === Screen.mfa) {
          dispatch(
            createFormErrTxt({
              msgId: "KGRERR040",
              msgArgs: [],
            })
          );
        } else {
          dispatch(
            createFormErrTxt({
              msgId: "KGRERR037",
              msgArgs: [],
            })
          );
        }
        break;
      case COGNITO_ERR_MSG.incorrectUserType:
        dispatch(
          createFormErrTxt({
            msgId: "KGRERR039",
            msgArgs: [userTypeLabel(window.location.hostname)],
          })
        );
        break;
      case COGNITO_ERR_MSG.lockedUser:
        dispatch(
          createFormErrTxt({
            msgId: "KGRERR038",
            msgArgs: [],
          })
        );
        break;
      case COGNITO_ERR_MSG.emailNotExist:
        dispatch(
          createFormErrTxt({
            msgId: "KGRERR044",
            msgArgs: [],
          })
        );
        break;
      case COGNITO_ERR_MSG.incorrectResetCode:
        dispatch(
          createFormErrTxt({
            msgId: "KGRERR041",
            msgArgs: [],
          })
        );
        break;
      case COGNITO_ERR_MSG.resetBlocked:
        dispatch(
          createFormErrTxt({
            msgId: "KGRERR045",
            msgArgs: [],
          })
        );
        break;
      case COGNITO_ERR_MSG.expiredTempPassword:
        dispatch(
          createFormErrTxt({
            msgId: "KGRERR070",
            msgArgs: [],
          })
        );
        break;
      default:
        if (err.message.includes(COGNITO_ERR_MSG.passwordPolicyNotSatisfied)) {
          dispatch(
            createFormErrTxt({
              msgId: "KGRERR043",
              msgArgs: [],
            })
          );
        } else {
          dispatch(
            createFormErrTxt({
              msgId: "KGRFAT001",
              msgArgs: [],
            })
          );
        }
    }
  };

  /**
   * 画面に応じた認証処理を行う
   */
  const doAuthenticationProcesses = async () => {
    if (validationCheck(screenType)) {
      switch (screenType) {
        case Screen.top:
          // dish without mfa
          // サインイン
          await Auth.signIn({
            username,
            password,
            validationData: {
              domain: window.location.hostname,
              ip: ipAddress,
            },
          })
            .then((user) => {
              setUser(user);
              switch (user.challengeName) {
                case "CUSTOM_CHALLENGE":
                  // MFA画面に遷移
                  setScreenType(Screen.mfa);
                  break;
                case "NEW_PASSWORD_REQUIRED":
                  // 初回ログイン画面に遷移
                  setScreenType(Screen.firstLogin);
                  setPassword("");
                  break;
                default:
                  // トップ画面に遷移
                  navigate(pathAfterLogin);
                  dispatch(api.util.invalidateTags(["Auth"]));
              }
            })
            .catch((err) => {
              handleErr(err);
            });
          break;
        case Screen.mfa:
          await Auth.sendCustomChallengeAnswer(user, code, {
            mfaSkip: String(checked),
          })
            .then((res) => {
              navigate(pathAfterLogin);
              dispatch(api.util.invalidateTags(["Auth"]));
            })
            .catch((err) => {
              handleErr(err);
              setScreenType(Screen.top);
              setPassword("");
            });
          break;
        case Screen.firstLogin:
          if (password !== confirmPassword) {
            dispatch(
              createFormErrTxt({
                msgId: "KGRERR042",
                msgArgs: [],
              })
            );
          } else {
            await Auth.completeNewPassword(user, password)
              .then((res) => {
                // トップ画面に遷移
                navigate(pathAfterLogin);
                dispatch(api.util.invalidateTags(["Auth"]));
              })
              .catch((err) => {
                handleErr(err);
              });
          }
          break;
        case Screen.chgPassEmail:
          await Auth.forgotPassword(username)
            .then((res) => {
              setScreenType(Screen.chgPassNewPass);
            })
            .catch((err) => {
              handleErr(err);
            });
          break;
        case Screen.chgPassNewPass:
          if (password !== confirmPassword) {
            dispatch(
              createFormErrTxt({
                msgId: "KGRERR042",
                msgArgs: [],
              })
            );
          } else {
            await Auth.forgotPasswordSubmit(username, code, password)
              .then((res) => {
                navigate(pathAfterLogin);
                dispatch(api.util.invalidateTags(["Auth"]));
              })
              .catch((err) => {
                handleErr(err);
              });
          }
      }
    }
  };

  /**
   * submit関数
   */
  const handleContinueBtnOnClick = () => {
    setLoading(true);
    dispatch(clearAllErrTxts());
    doAuthenticationProcesses().finally(() => {
      setLoading(false);
    });
  };

  const topDescription = () => {
    return (
      "こちらは" +
      userTypeLabel(window.location.hostname) +
      "専用ログイン画面です"
    );
  };

  const setScreen = () => {
    switch (screenType) {
      case Screen.top:
        return (
          <FormArea description={topDescription()}>
            <UsernameField
              errText={getErrorText("username", formFieldErrTxts)}
              username={username}
              disabled={false}
              onChange={handleUsernameOnChange}
            />
            <PasswordField
              label={LABELS.PASSWORD}
              errText={getErrorText("password", formFieldErrTxts)}
              password={password}
              onChange={handlePasswordOnChange}
            />
            <Button variant="link" onClick={handleForgotPasswordBtnOnClick}>
              パスワードをお忘れですか？
            </Button>
          </FormArea>
        );
      case Screen.mfa:
        return (
          <FormArea description="コードを入力してください">
            <UsernameField errText="" username={username} disabled={true} />
            <CodeField
              errText={getErrorText("code", formFieldErrTxts)}
              code={code}
              onChange={handleCodeOnChange}
            />
            <Checkbox
              onChange={({ detail }) => setChecked(detail.checked)}
              checked={checked}
            >
              ログイン時の認証コード入力を{MFA_SKIP_DAYS}日間スキップする
            </Checkbox>
          </FormArea>
        );
      case Screen.firstLogin:
        return (
          <FormArea description="新しいパスワードを入力してください">
            <PasswordField
              label={LABELS.NEW_PASSWORD}
              errText={getErrorText("newPassword", formFieldErrTxts)}
              password={password}
              onChange={handlePasswordOnChange}
            />
            <PasswordField
              label={LABELS.NEW_PASSWORD_CONFIRMATION}
              errText={getErrorText("confirmNewPassword", formFieldErrTxts)}
              password={confirmPassword}
              onChange={handleConfirmPasswordOnChange}
            />
            <PasswordPolicy />
          </FormArea>
        );
      case Screen.chgPassEmail:
        return (
          <FormArea description="入力したメールアドレスにパスワードリセット用の認証コードを送信します">
            <UsernameField
              errText={getErrorText("username", formFieldErrTxts)}
              username={username}
              disabled={false}
              onChange={handleUsernameOnChange}
            />
          </FormArea>
        );
      case Screen.chgPassNewPass:
        return (
          <FormArea description="送信された認証コードと新しいパスワードを入力してください">
            <CodeField
              errText={getErrorText("code", formFieldErrTxts)}
              code={code}
              onChange={handleCodeOnChange}
            />
            <PasswordField
              label={LABELS.NEW_PASSWORD}
              errText={getErrorText("newPassword", formFieldErrTxts)}
              password={password}
              onChange={handlePasswordOnChange}
            />
            <PasswordField
              label={LABELS.NEW_PASSWORD_CONFIRMATION}
              errText={getErrorText("confirmNewPassword", formFieldErrTxts)}
              password={confirmPassword}
              onChange={handleConfirmPasswordOnChange}
            />
            <PasswordPolicy />
          </FormArea>
        );
    }
  };

  return (
    <Grid
      gridDefinition={[
        { colspan: { default: 0, xxs: 1, xs: 2, s: 3, m: 4 } },
        { colspan: { default: 12, xxs: 10, xs: 8, s: 6, m: 4 } },
        { colspan: { default: 0, xxs: 1, xs: 2, s: 3, m: 4 } },
      ]}
    >
      <Box></Box>
      <Container>
        <Grid
          gridDefinition={[{ colspan: 1 }, { colspan: 10 }, { colspan: 1 }]}
        >
          <Box></Box>
          <Box textAlign="center">
            <SpaceBetween size="xs">
              {setScreen()}
              <Button
                loading={loading}
                variant="primary"
                onClick={handleContinueBtnOnClick}
              >
                続ける
              </Button>
            </SpaceBetween>
          </Box>
          <Box></Box>
        </Grid>
      </Container>
      <Box></Box>
    </Grid>
  );
};

export default SignIn;
